import { Button, Form, Input, Select } from "antd";
import { useTranslation } from "react-i18next";

import { getCountryCodes, COUNTRY_NAMES } from "@/v1/constants/countryNames";

type Address = {
  street: string;
  postCode: string;
  city: string;
  country: string;
};

type Props = {
  initialValues: Address;
  onFinish: (address: Address) => void;
  onReset?: () => void;
};

export function EditAddressForm({ initialValues, onFinish, onReset }: Props) {
  const [form] = Form.useForm();
  const { i18n } = useTranslation();
  const countryLang = i18n.language.startsWith("de") ? "de" : "en";

  const handleFinish = (values: Address) => {
    onFinish(values);
  };

  const handleReset = () => {
    form.resetFields();
    onReset?.();
  };

  return (
    <Form
      form={form}
      initialValues={initialValues}
      onFinish={handleFinish}
      onReset={handleReset}
      layout="vertical"
    >
      <Form.Item
        name="street"
        rules={[
          { required: true, message: "Street & House Number is required" },
        ]}
        className="mb-2"
        label="Street & House Number"
      >
        <Input type="text" placeholder="Eg. Berliner Straße 1" autoFocus />
      </Form.Item>
      <Form.Item
        name="postCode"
        rules={[
          { required: true, message: "Post Code is required" },
          {
            validator: (_, value) => {
              const country = form.getFieldValue("country");

              if (country !== "DE" || !value) {
                return Promise.resolve();
              }

              return /^[0-9]{5}$/.test(value)
                ? Promise.resolve()
                : Promise.reject(new Error("Post Code must be 5 digits"));
            },
          },
        ]}
        className="mb-2"
        label="Post Code"
      >
        <Input type="text" placeholder="Eg. 12345" />
      </Form.Item>
      <Form.Item
        name="city"
        rules={[{ required: true, message: "City is required" }]}
        className="mb-2"
        label="City"
      >
        <Input type="text" placeholder="Eg. Berlin" />
      </Form.Item>
      <Form.Item
        name="country"
        rules={[{ required: true, message: "Country is required" }]}
        className="mb-1"
        label="Country"
      >
        <Select
          showSearch
          optionFilterProp="label"
          filterOption={(input, option) =>
            (option?.label ?? "")
              .toString()
              .toLowerCase()
              .includes(input.toLowerCase())
          }
          options={getCountryCodes().map((code) => ({
            value: code,
            label: COUNTRY_NAMES[code]?.[countryLang] ?? code,
          }))}
        />
      </Form.Item>
      <Form.Item>
        <Button type="primary" htmlType="submit">
          Save
        </Button>
        <Button htmlType="reset" className="ml-2">
          Cancel
        </Button>
      </Form.Item>
    </Form>
  );
}
